/**
 * „Essenzspiegel vorlesen“ startet den fertigen Player. (27.09.2026, Ticket 2698)
 *
 * Martin: *„Wenn er sagt ‚Essenzspiegel vorlesen‘, soll das System direkt zum
 * bereits transkribierten Essenzspiegel-Player wechseln/starten, statt den
 * Text neu zu generieren/vorzulesen.“* Das Modell ruft dafür das Werkzeug
 * `abspielen` (tools/abspielen.ts) — es prüft nur, ob es etwas gibt. Hier
 * steht, was das Gerät daraus macht.
 *
 * Der Ablauf:
 *   1. dispatchToolCard (app.ts, nur live) merkt den Start vor
 *      (`merkeAbspielen`). Aus dem Verlauf startet nichts.
 *   2. Ist der Text des Zuges komplett, reicht der Weg (Sprechtaste oder
 *      Chat) sein Vorlesen herein (`abspielenNachStimme`). Gestartet wird
 *      erst, wenn Vesperas eigener Satz verklungen ist — sonst sprächen
 *      Stimme und Player gleichzeitig. Der Sprechtasten-Vorrang (Ticket
 *      2551) hilft hier nicht: der Player läuft über die NarrationEngine,
 *      nicht über beginTtsStream.
 *   3. Lässt der Browser ohne frischen Tipp keinen Ton zu, wird der Player
 *      sichtbar angeboten („Antippen zum Abspielen“), statt still zu
 *      scheitern oder „Vorlesen gerade nicht möglich“ zu behaupten.
 *
 * ⚠️ Gewartet wird auf das Vorlesen DIESES Zuges (TtsQueue.idle) und nicht
 *    auf `tts-audio-end`: Die Karte kommt, BEVOR Vespera ihren Satz sagt —
 *    in dem Moment klingt noch nichts, und „sonst sofort“ hätte den Player
 *    unter ihre Stimme gelegt. idle() endet bei Ende UND Abbruch; das
 *    Ereignis fehlt dagegen, wenn nichts gespielt wurde.
 * ⚠️ EIN Besitzer: nur diese Datei hält den vorgemerkten Start. Ein neuer
 *    Zug (eroeffneZug) und ein neuer Druck auf die Sprechtaste verwerfen
 *    ihn — sonst spielte die Meditation in seine nächste Aufnahme.
 */
import { absatz, aktionsKnopf, aktionsLeiste, baueKarte } from "./karte.js";
import { tonOhneTippMoeglich } from "./audio-feedback.js";
import { icon } from "./icons.js";
import { auspacken } from "./schritte.js";
import { grundInWorten } from "./vorlese-verlust.js";

/** Das Werkzeug, mit dem das Modell den Start anfordert. */
export const ABSPIEL_WERKZEUG = "abspielen";

// 07.10.2026: „lied“ — ein erzeugtes Lied aus der Mediathek („Spiel das Lied noch einmal“).
// 19.10.2026: „datei“ — eine Textdatei wörtlich im Player vorlesen (frontend/lib/datei-vorlese-karte.ts).
export type AbspielZiel = "spiegel" | "meditation" | "alltag" | "lied" | "datei";

/** So heißt das Ziel auf dem Angebot. */
const ZIEL_TITEL: Readonly<Record<AbspielZiel, string>> = {
  spiegel: "Essenz-Spiegel",
  meditation: "Morgenmeditation",
  alltag: "Mein Alltag",
  lied: "Lied",
  datei: "Datei",
};

const ZIELE: ReadonlySet<string> = new Set(Object.keys(ZIEL_TITEL));
const TAG_MUSTER = /^\d{4}-\d{2}-\d{2}$/;

/** Welches Ziel trägt dieses Ergebnis? Auch der Kartenbau fragt hier. */
export function abspielZiel(result: unknown): AbspielZiel | null {
  const z = auspacken(result).ziel;
  return typeof z === "string" && ZIELE.has(z) ? (z as AbspielZiel) : null;
}

export interface AbspielAuftrag {
  ziel: AbspielZiel;
  /** Ein bestimmter Tag — null heißt: der aktuelle. */
  tag: string | null;
}

/** Nur ein gelungenes Ergebnis ist ein Auftrag. */
export function abspielAuftrag(tool: string, isError: boolean, result: unknown): AbspielAuftrag | null {
  if (tool !== ABSPIEL_WERKZEUG || isError) return null;
  const r = auspacken(result);
  const ziel = abspielZiel(r);
  if (r.ok !== true || !ziel) return null;
  return { ziel, tag: typeof r.tag === "string" && TAG_MUSTER.test(r.tag) ? r.tag : null };
}

/** Ziele, deren Karte den Player trägt und über ihn startet (merkeKartenStart). */
export const KARTE_TRAEGT_PLAYER: ReadonlySet<AbspielZiel> = new Set<AbspielZiel>(["meditation", "lied", "datei"]);

/**
 * Keine eigene Karte: ein gelungener Start von Spiegel oder Alltag — der
 * Player selbst ist die Antwort. Die Meditation zeigt ihre Karte (dort
 * steht der Player), ein Fehlschlag seine Zeile.
 */
export function abspielenOhneKarte(tool: string, result: unknown): boolean {
  if (tool !== ABSPIEL_WERKZEUG) return false;
  const r = auspacken(result);
  const ziel = abspielZiel(r);
  // Meditation und Lied bringen ihre Karte mit dem Player mit.
  return r.ok === true && ziel !== null && !KARTE_TRAEGT_PLAYER.has(ziel);
}

/** Was ein Ziel zum Starten braucht. */
export interface Abspieler {
  /** Startet den Player — derselbe Weg wie sein eigener Knopf. */
  spielen(): void;
  /** Der Ton ist gesperrt: sichtbar anbieten statt still scheitern. */
  anbieten(): void;
}

/*
 * Der Start einer Meditationskarte je Aufruf. Der Kartenbau (tool-cards.ts)
 * merkt ihn, der Live-Weg nimmt ihn — die Karte selbst startet nie, denn aus
 * dem Verlauf darf nichts losspielen. Wenige Einträge reichen: ein Start
 * wird gleich nach dem Bau genommen, übrig bleiben nur Karten aus dem
 * Verlauf.
 */
const kartenStarts = new Map<string, Abspieler>();
const KARTEN_STARTS_MAX = 8;

export function merkeKartenStart(callId: string, abspieler: Abspieler): void {
  if (!callId) return;
  kartenStarts.delete(callId);
  kartenStarts.set(callId, abspieler);
  while (kartenStarts.size > KARTEN_STARTS_MAX) {
    const aeltester = kartenStarts.keys().next().value;
    if (aeltester === undefined) break;
    kartenStarts.delete(aeltester);
  }
}

export function nimmKartenStart(callId: string): Abspieler | null {
  const a = kartenStarts.get(callId) ?? null;
  kartenStarts.delete(callId);
  return a;
}

let wartet: { abspieler: Abspieler } | null = null;

/** Ein neuer Zug oder ein neuer Druck: kein Start von vorhin. */
export function zugOhneAbspielen(): void {
  wartet = null;
}

/** dispatchToolCard (live): den Start vormerken — er folgt der eigenen Stimme. */
export function merkeAbspielen(abspieler: Abspieler): void {
  wartet = { abspieler };
}

/** Steht ein Start aus? Dann öffnet das Vorlese-Ende kein Mikro. */
export function abspielenWartet(): boolean {
  return wartet !== null;
}

/**
 * Der Text des Zuges ist komplett.
 *
 * @param stimme endet, wenn das Vorlesen dieses Zuges verklungen oder
 *   abgebrochen ist (TtsQueue.idle) — null, wenn nichts vorgelesen wird.
 * @param tonFrei darf jetzt ohne Tipp Ton kommen? Nur Tests reichen hier
 *   etwas anderes herein.
 */
export function abspielenNachStimme(
  stimme: Promise<unknown> | null,
  tonFrei: () => Promise<boolean> = tonOhneTippMoeglich,
): void {
  const mein = wartet;
  if (!mein) return;
  const los = async (): Promise<void> => {
    // Kam inzwischen ein neuer Zug oder Druck, gilt dieser Start nicht mehr.
    if (wartet !== mein) return;
    wartet = null;
    let frei = false;
    try {
      frei = await tonFrei();
    } catch {
      frei = false;
    }
    if (frei) mein.abspieler.spielen();
    else mein.abspieler.anbieten();
  };
  if (!stimme) {
    void los();
    return;
  }
  void stimme.then(los, los);
}

/** Der Satz, der sagt, warum getippt werden muss — derselbe wie beim Vorlesen. */
export function baueTippHinweis(): HTMLElement {
  return absatz(grundInWorten("nicht-erlaubt"), "hint");
}

/**
 * Das Angebot im Chat, wenn der Ton gesperrt ist. Der Tipp ist die Geste,
 * die der Browser verlangt, und startet über denselben Weg wie der
 * Selbststart. Danach ist das Angebot erledigt und geht.
 */
export function baueAntippAngebot(ziel: AbspielZiel, spielen: () => void): HTMLElement {
  const titel = ZIEL_TITEL[ziel];
  const el = baueKarte(ABSPIEL_WERKZEUG, titel);
  el.appendChild(baueTippHinweis());
  aktionsLeiste(el).appendChild(
    aktionsKnopf({
      text: "Antippen zum Abspielen",
      primaer: true,
      symbol: "play",
      name: `Antippen zum Abspielen: ${titel}`,
      onClick: () => {
        el.remove();
        spielen();
      },
    }),
  );
  return el;
}
